// RouteProgress — the navigation progress bar: a slim brand strip that signals a
// route transition (a lazy route's chunk fetch, or a router data load) is in
// flight, in the gap before the destination can render its own skeleton.
//
// Motion (DESIGN-TOKENS §Motion): the GitHub/Turbo trickle, as smooth CSS
// transitions. A route load has no measurable progress, so while pending the
// fill TRICKLES toward a ~90% cap on a long decelerating curve (it lingers, never
// self-completes); on completion it RUSHES to 100% then the strip fades — the
// rush is the honest "done" signal, fired only when the navigation truly
// resolved. The strip is box-capped to `pageBox`, so on ultra-wide it sits over
// the content column, not the calm canvas wings — matching every shell band
// (Model B).
//
// Shell-owned: mounts once in RootLayout (inside RouterProvider, so
// useNavigation is available) and covers every route transition. Purely
// decorative (aria-hidden) — the destination page owns its own loading
// announcement; a top strip narrated on every nav would be noise.
//
// A short show-delay suppresses the flash on instant transitions (the common
// case): the strip only paints if loading outlasts the delay. Under reduced
// motion the sweep is replaced by a static full bar (the no-preference rule in
// index.css) — its presence still signals "loading".

import { useEffect, useRef, useState } from 'react';
import { useNavigation } from 'react-router-dom';
import { pageBox } from './pageBox.js';

// Only paint for navigations slower than this — instant transitions show nothing
// (showing the bar on every click is what makes top loaders feel slow).
const SHOW_DELAY_MS = 180;
// How long `done` lasts before reset to idle — covers the rush (300ms) + the
// staggered opacity fade (300ms delay + --duration-fast), so the bar is fully
// gone before it re-arms. Mirrors the `.route-progress` transitions in index.css.
const DONE_FADE_MS = 600;

export type RouteProgressPhase = 'idle' | 'loading' | 'done';

// Presentational strip — fills its parent's width, driven entirely by `phase`.
// Split out from the nav-wired container so the motion is exercisable in the
// workbench without a router. Decorative by contract.
export function RouteProgressBar({ phase }: { phase: RouteProgressPhase }) {
  return (
    <div className="route-progress" data-state={phase} aria-hidden>
      <div className="route-progress__fill" />
    </div>
  );
}

export function RouteProgress() {
  const navigation = useNavigation();
  const loading = navigation.state !== 'idle';
  const [phase, setPhase] = useState<RouteProgressPhase>('idle');
  // Whether the bar actually painted this cycle — a nav that resolves inside the
  // delay never showed, so it must reset silently (no done-fill flash).
  const shownRef = useRef(false);

  useEffect(() => {
    if (loading) {
      const id = setTimeout(() => {
        shownRef.current = true;
        setPhase('loading');
      }, SHOW_DELAY_MS);
      return () => clearTimeout(id);
    }
    // Became idle.
    if (!shownRef.current) return;
    shownRef.current = false;
    // Defer out of the effect body (next frame) so the done-fill paints, then
    // reset to idle after the fade resolves.
    const raf = requestAnimationFrame(() => setPhase('done'));
    const id = setTimeout(() => setPhase('idle'), DONE_FADE_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(id);
    };
  }, [loading]);

  return (
    <div className="fixed inset-x-0 top-0 z-progress pointer-events-none">
      <div className={pageBox}>
        <RouteProgressBar phase={phase} />
      </div>
    </div>
  );
}
